import {
  IssueManagementMode,
  PanelDisplayRequest,
  I3DCallbacks,
  TcIssueManagement,
} from '@trimble/tc-issue-management-ui';
import {
  on,
  createResource,
  createSignal,
  createEffect,
  createMemo,
  JSX,
  onCleanup,
  Show,
} from 'solid-js';
import { ErrorState } from '../components/LoadingState';
import { useAuth } from '@trimble/trimble-id-solidjs';
import {
  collectVisibleModels,
  getVisiblePointCloudIds,
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
  usePaneContext,
} from '@mobile-mapping/providers';
import { useTranslations } from '@trimble/solidjs-utils';
import {
  IssueMarkerIconCoordinator,
  IssueMarkerPickCoordinator,
} from '@trimble/tc-issue-management-web3d-utilities';
import { t } from '@trimble/i18n';
import { Spinner } from '@tcweb/connect-ui';
import { ILogger } from '@trimble/logging';
import { ViewsCapabilityProvider } from '@trimble/web3d-providers';
import { useLogger } from '@trimble/solidjs-utils';
import { IssueManagementCbStore } from './IssueManagementCbStore';
import { useConnectProject } from '@trimble/connect-project-providers';
import { createNumarkIssueReservedViewDataConfig } from './create-numark-issue-reserved-view-data-config';

function logger(): ILogger {
  return useLogger().getLogger('IssueManagementTab');
}

type CreateTopicRequest = {
  readonly type: 'create-topic';
  readonly id: number;
};

export const IssueManagementTab = (props: {
  viewsProvider: ViewsCapabilityProvider;
  createTopicRequest?: CreateTopicRequest;
  onCreateTopicRequestConsumed?(): void;
}): JSX.Element => {
  const { isProductionEnvironment, tcTcpsApi, scanDataApi } = useApiServices();
  const auth = useAuth();
  const { selectedProject, selectedRegion } = useConnectProject();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const { dataState, dataLoadingService, activeItems } = useDataContext();
  const { layouts, setLayouts } = usePaneContext();
  const { translations, translationsReady, refetchTranslations } =
    useTranslations();

  const [showAllMarker, setShowAllMarker] = createSignal(true);
  const [displayRequest, setDisplayRequest] = createSignal<
    PanelDisplayRequest | undefined
  >(undefined);
  const [issueManagementInstanceId, setIssueManagementInstanceId] =
    createSignal(0);

  const numarkReservedViewData = createNumarkIssueReservedViewDataConfig({
    tcTcpsApi,
    scanDataApi,
    getRegion: () => selectedRegion(),
    getProjectId: () => selectedProject()?.id,
    capabilityRegistry,
    layouts,
    activeItems,
    dataState,
    logger: logger(),
  });

  const savePointCloudsForCreatedView = (viewId: string): void => {
    const region = selectedRegion();
    const project = selectedProject();
    if (!region || !project) return;

    const visiblePointCloudIds = getVisiblePointCloudIds(dataState) ?? [];
    scanDataApi
      .savePointCloudsForView(region, project.id, viewId, visiblePointCloudIds)
      .then(() => {
        logger().debug('Point clouds saved for view', {
          viewId,
          visiblePointCloudIds,
        });
      })
      .catch((error: unknown) => {
        logger().error('Failed to save point clouds for view', {
          viewId,
          error,
        });
      });
  };

  createEffect(
    on(
      () => props.createTopicRequest,
      (request) => {
        if (!request) return;
        setIssueManagementInstanceId(request.id);
        setDisplayRequest({ mode: IssueManagementMode.Create });
        props.onCreateTopicRequestConsumed?.();
      }
    )
  );

  const [accessToken, { refetch: refetchAccessToken }] = createResource(
    async () => {
      return await auth().getAccessTokenSilently();
    }
  );

  const resourceError = (): Error | string | undefined => {
    if (accessToken.error) return accessToken.error as Error;
    if (translations.error) return translations.error as Error;
    return undefined;
  };

  const retry = (): void => {
    if (accessToken.error) void refetchAccessToken();
    if (translations.error) refetchTranslations();
  };

  return (
    <Show
      when={!resourceError()}
      fallback={
        <div
          class="flex flex-col items-center justify-center gap-3 w-full h-full p-4 bg-white"
          data-testid="issue-management-error"
        >
          <ErrorState
            error={resourceError()}
            prefix={t('mobileMapping.issueManagement.loadError')}
          />
          <button
            class="text-sm text-blue-600 underline cursor-pointer"
            onClick={retry}
            data-testid="issue-management-retry"
          >
            {t('mobileMapping.issueManagement.retry')}
          </button>
        </div>
      }
    >
      <Show
        when={
          accessToken() &&
          selectedProject() &&
          translationsReady() &&
          selectedRegion()
            ? {
                selectedProject: selectedProject()!,
                selectedRegion: selectedRegion()!,
                accessToken: accessToken()!,
                issueManagementInstanceId: issueManagementInstanceId(),
              }
            : undefined
        }
        fallback={
          <div class="w-full h-[300px]">
            <Spinner show={true} />
          </div>
        }
        keyed
      >
        {(data) => {
          const iconCoordinator = capabilityRegistry.get(
            IssueMarkerIconCoordinator
          )();
          const pickCoordinator = capabilityRegistry.get(
            IssueMarkerPickCoordinator
          )();

          onCleanup(() => {
            pickCoordinator?.cancelPick();
            iconCoordinator?.removeMarker();
            iconCoordinator?.setMarkers([]);
            iconCoordinator?.setSelectedMarkerId(null);
          });

          const callbacks = createMemo<I3DCallbacks>(() => ({
            ...IssueManagementCbStore({
              projectId: data.selectedProject.id,
              capabilityRegistry,
              dataState,
              region: data.selectedRegion,
              tcTcpsApi,
              dataLoadingService,
              layouts,
              setLayouts,
              pushSnapshot: numarkReservedViewData.pushSnapshot,
              beginCreateCapture: numarkReservedViewData.beginCreateCapture,
              pushCreateSnapshot: numarkReservedViewData.pushCreateSnapshot,
            }),
            onShowAllMarkerChange: (showAll) => setShowAllMarker(showAll),
            onIssueViewCreatedSuccess: (viewId) => {
              savePointCloudsForCreatedView(viewId);
            },
            onCommentViewCreatedSuccess: (viewId) => {
              savePointCloudsForCreatedView(viewId);
            },
            onCommentViewCaptureCancelled: (issueId) => {
              numarkReservedViewData.popLastSnapshot(issueId);
            },
            onIssueViewCreateError: (issueId) => {
              numarkReservedViewData.popLastSnapshot(issueId);
            },
          }));
          return (
            <TcIssueManagement
              projectId={data.selectedProject.id}
              accessToken={data.accessToken}
              environment={isProductionEnvironment ? 'prod' : 'stage'}
              threeDCallbacks={callbacks()}
              reservedViewDataConfig={
                numarkReservedViewData.reservedViewDataConfig
              }
              featureConfig={{ issueViewPreview: { enabled: true } }}
              modelIds={collectVisibleModels(dataState)}
              showAllMarker={showAllMarker()}
              displayRequest={displayRequest()}
            />
          );
        }}
      </Show>
    </Show>
  );
};
